import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";

export const inputCls = "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

export function L({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-1.5">{label}</div>
      {children}
      {hint && <div className="text-[10px] text-[#8a8478] mt-1">{hint}</div>}
    </label>
  );
}

export function SectionCard({ id, title, sec, onToggle, onHeading, children, alwaysOn }) {
  return (
    <div className="bg-white border border-[#e5e0d8] rounded-sm" data-testid={`ml-sec-${id}`}>
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#e5e0d8] bg-[#f9f8f6]">
        <div className="font-serif text-lg text-[#1c1a17]">{title}</div>
        {!alwaysOn && (
          <label className="flex items-center gap-2 text-xs font-semibold text-[#234333] cursor-pointer">
            <input type="checkbox" checked={!!sec.visible} onChange={(e) => onToggle(e.target.checked)} data-testid={`ml-visible-${id}`} />
            {sec.visible ? "Shown" : "Hidden"}
          </label>
        )}
      </div>
      <div className={`p-5 space-y-4 ${!alwaysOn && !sec.visible ? "opacity-50" : ""}`}>
        {onHeading && <L label="Section heading"><input className={inputCls} value={sec.heading} maxLength={120} onChange={(e) => onHeading(e.target.value)} data-testid={`ml-heading-${id}`} /></L>}
        {children}
      </div>
    </div>
  );
}

export function RteField({ label, value, onChange, testid, placeholder }) {
  return <L label={label}><RichTextEditor value={value} onChange={onChange} placeholder={placeholder} testid={testid} /></L>;
}

export function Repeater({ items, onChange, blank, render, addLabel, testid }) {
  const upd = (i, patch) => onChange(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const move = (i, d) => { const a = [...items]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; onChange(a); };
  return (
    <div className="space-y-3" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="border border-[#e5e0d8] rounded-sm p-3 bg-[#fbf8f1] relative" data-testid={`${testid}-item-${i}`}>
          <div className="absolute top-2 right-2 flex gap-1">
            <button type="button" onClick={() => move(i, -1)} className="p-1 hover:bg-white rounded-sm" aria-label="Move up"><ArrowUp size={13} /></button>
            <button type="button" onClick={() => move(i, 1)} className="p-1 hover:bg-white rounded-sm" aria-label="Move down"><ArrowDown size={13} /></button>
            <button type="button" onClick={() => onChange(items.filter((_, j) => j !== i))} className="p-1 hover:bg-red-50 text-red-700 rounded-sm" data-testid={`${testid}-remove-${i}`} aria-label="Remove"><Trash2 size={13} /></button>
          </div>
          <div className="pr-20">{render(it, (patch) => upd(i, patch), i)}</div>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, { ...blank }])} className="inline-flex items-center gap-1.5 border border-dashed border-[#234333] text-[#234333] text-xs font-semibold px-3 py-2 rounded-sm" data-testid={`${testid}-add`}>
        <Plus size={13} />{addLabel}
      </button>
    </div>
  );
}

export function BannerField({ url, onChanged }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.(jpe?g|png|webp)$/i.test(f.name)) return toast.error("Only JPG, PNG or WEBP images are allowed");
    if (f.size === 0 || f.size > 3 * 1024 * 1024) return toast.error("Image must be between 1 byte and 3 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f, f.name);
      const { data } = await api.post("/admin/marathon/landing/banner", fd);
      onChanged(data); toast.success("Header image updated");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed"); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the landing page header image?")) return;
    const { data } = await api.delete("/admin/marathon/landing/banner");
    onChanged(data); toast.success("Header image removed");
  };
  return (
    <L label="Header image" hint="JPG, PNG or WEBP · max 3 MB · wide image recommended (1920×900). If empty, the Marathon Event banner from Masters is used.">
      <div className="relative aspect-[16/6] bg-[#08363A] rounded-sm overflow-hidden">
        {url ? <img src={url} alt="Landing header" className="absolute inset-0 w-full h-full object-cover" data-testid="ml-banner-preview" />
          : <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm" data-testid="ml-banner-empty">No image uploaded</div>}
      </div>
      <input ref={ref} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" onChange={pick} data-testid="ml-banner-input" />
      <div className="flex gap-2 mt-2">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} className="bg-[#234333] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm disabled:opacity-60" data-testid="ml-banner-upload">{busy ? "Uploading…" : url ? "Replace image" : "Upload image"}</button>
        {url && <button type="button" onClick={remove} className="border border-red-200 text-red-700 text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-sm" data-testid="ml-banner-remove">Remove</button>}
      </div>
    </L>
  );
}
